import { Component, forwardRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { Partner, PartnerLoadOptions } from '../models';
import { PartnerSelectorComponent } from '../partner-selector.component';
import { PartnerService } from '../services/partner.service';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'ep-partner-selector-multiple',
    templateUrl: './partner-selector-multiple.component.html',
    styleUrls: ['./partner-selector-multiple.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PartnerSelectorMultipleComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [PartnerSelectorComponent, AsyncPipe]
})
export class PartnerSelectorMultipleComponent implements ControlValueAccessor {
    @ViewChild('partnerSelector') partnerSelector: PartnerSelectorComponent;

    private partnerIds = new BehaviorSubject<number[]>([]);
    partnerIds$ = this.partnerIds.asObservable();

    partners$ = this.partnerIds$.pipe(
        mergeMap(ids => this.loadPartners(ids)),
        map(e => e.sort((a, b) => a.EdiPortalId > b.EdiPortalId ? 1 : -1))
    );

    disabled = false;

    onChange = (_: any) => { };
    onTouched = () => { };

    constructor(
        private toastService: ToastService,
        private partnerService: PartnerService) {
    }

    async loadPartners(partnerIds: number[]): Promise<Partner[]> {
        try {
            const plo = new PartnerLoadOptions(false);
            plo.LoadMainIdentification = true;
            plo.UseCache = true;

            return await this.partnerService.getPartners(partnerIds, plo);
        }
        catch (err) {
            console.log(err);
            this.toastService.showToast('Failed to load partners', ToastType.error);
            return undefined;
        }
    }

    selectedPartnerIdChange(ev: any): void {
        // ev is null when selector component is reset
        if (ev) {
            const partnerId = ev as number;

            // Don't add duplicates
            if (!this.partnerIds.value.includes(partnerId)) {
                this.partnerIds.next([...this.partnerIds.value, partnerId]);
                this.onChange(this.partnerIds.value);
            }

            // Clear the partner selector
            this.partnerSelector.reset();
        }
    }

    remove(id: number): void {
        this.partnerIds.next(this.partnerIds.value.filter(e => e !== id));
        this.onChange(this.partnerIds.value);
    }

    // Begin ControlValueAccessor //

    writeValue(value: any): void {
        if (!value) {
            this.partnerIds.next([]);
        }
        else if (Array.isArray(value)) {
            this.partnerIds.next(value);
        }
    }

    registerOnChange(fn: (_: any) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    // End ControlValueAccessor //
}
